import { Clock, Instagram, MapPin } from 'lucide-react';

import { BUSINESS_HOURS, CONTACT, SITE } from '@/config/site';
import { WA_LINKS } from '@/lib/whatsapp';

import { PawIcon, WhatsAppIcon } from '../common/icons';
import { Section, SectionHeading } from '../common/Section';
import { Button } from '../ui/button';

/**
 * Contact block.
 *
 * The original artwork for this section was flagged as visually overloaded, so
 * the same information is rebuilt as real markup: two channels, opening hours
 * and coverage area, each with room to breathe. As live text it is also
 * selectable, translatable, indexable and readable by a screen reader.
 */
export function ContactBlock() {
  return (
    <Section id="contato" tone="cream" size="md" container="md">
      <SectionHeading
        eyebrow="Fale com a gente"
        title="Estamos ainda mais perto de você"
        description="Tire dúvidas, peça indicação de ração ou acompanhe seu pedido pelos nossos canais oficiais."
        className="mb-10"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <article className="flex flex-col gap-4 rounded-3xl border-2 border-sand-300 bg-white p-6 shadow-card">
          <span className="grid size-12 place-items-center rounded-2xl bg-whats-500">
            <WhatsAppIcon className="size-6 text-white" />
          </span>

          <div>
            <h3 className="font-display text-lg font-bold text-brown-900">WhatsApp</h3>
            <p className="font-display text-xl font-extrabold text-whats-600">
              {CONTACT.whatsappDisplay}
            </p>
          </div>

          <p className="flex-1 text-sm leading-relaxed text-brown-700">
            Faça pedidos, consulte disponibilidade e receba atendimento rápido de gente de verdade.
          </p>

          <Button asChild variant="whatsapp" full>
            <a href={WA_LINKS.general} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon aria-hidden="true" />
              Clique e chame
            </a>
          </Button>
        </article>

        <article className="flex flex-col gap-4 rounded-3xl border-2 border-sand-300 bg-white p-6 shadow-card">
          <span
            className="grid size-12 place-items-center rounded-2xl"
            style={{
              background:
                'linear-gradient(135deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
            }}
          >
            <Instagram className="size-6 text-white" aria-hidden="true" />
          </span>

          <div>
            <h3 className="font-display text-lg font-bold text-brown-900">Instagram</h3>
            <p className="font-display text-xl font-extrabold text-orange-600">
              @{CONTACT.instagramHandle}
            </p>
          </div>

          <p className="flex-1 text-sm leading-relaxed text-brown-700">
            Novidades, promoções e conteúdo sobre nutrição pet direto no seu feed.
          </p>

          <Button asChild variant="dark" full>
            <a href={CONTACT.instagramUrl} target="_blank" rel="noopener noreferrer">
              <Instagram aria-hidden="true" />
              Clique e acesse
            </a>
          </Button>
        </article>
      </div>

      <div className="mt-4 grid gap-6 rounded-3xl border-2 border-sand-300 bg-cream-300 p-6 sm:grid-cols-2">
        <div className="flex gap-3">
          <Clock className="mt-0.5 size-5 shrink-0 text-caramel-500" aria-hidden="true" />
          <div>
            <h3 className="font-display text-sm font-bold text-brown-900">
              Horário de atendimento
            </h3>
            <ul className="mt-1.5 flex flex-col gap-0.5 text-sm text-brown-700">
              {BUSINESS_HOURS.map((slot) => (
                <li key={slot.days}>
                  <span className="font-semibold">{slot.days}:</span> {slot.hours}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex gap-3">
          <MapPin className="mt-0.5 size-5 shrink-0 text-caramel-500" aria-hidden="true" />
          <div>
            <h3 className="font-display text-sm font-bold text-brown-900">Área de entrega</h3>
            <p className="mt-1.5 text-sm text-brown-700">
              {SITE.serviceArea}. Confirme a cobertura do seu bairro pelo WhatsApp antes de fechar o
              pedido.
            </p>
            <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-caramel-600">
              <PawIcon className="size-3.5" />
              Combinamos dia e horário que funcionem para você.
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
}
